import React, { useMemo, useState } from 'react';
import {
  OUTREACH_EMAIL_TYPES,
  buildWhatsAppMessage,
  lastOutreach,
  sendOutreach,
  suggestEmailType,
  supplierWhatsAppNumber,
} from '../../lib/consoleOutreach';

// Chasing one supplier, from the screen where you decided he needs chasing.
//
// Before this, the only outreach on the verification screen was a side effect:
// "Request more information" emailed him as part of a DECISION that also moved
// his status. There was no way to simply ask a supplier who had uploaded nothing
// to upload something — which is 14 of the 24 accounts on record.
//
// The WhatsApp message is written in the SUPPLIER's language, never the admin's,
// and it names the fields he is actually missing rather than saying "your file is
// incomplete". It opens his chat with the text ready; the admin presses send.

const fmtWhen = (v, isAr) => (v
  ? new Date(v).toLocaleDateString(isAr ? 'ar' : 'en', { day: 'numeric', month: 'short' })
  : '');

export default function SupplierOutreach({ row, journey, history, user, isAr, flash, onSent }) {
  const [emailType, setEmailType] = useState(() => suggestEmailType(row, journey));
  const [busy, setBusy] = useState(null);
  const [preview, setPreview] = useState(false);

  const waNumber = supplierWhatsAppNumber(row);
  const waMessage = useMemo(() => buildWhatsAppMessage(row, row.completeness), [row]);
  const last = useMemo(() => lastOutreach(history), [history]);
  const suggested = useMemo(() => suggestEmailType(row, journey), [row, journey]);

  const runEmail = async () => {
    setBusy('email');
    const res = await sendOutreach({ channel: 'email', row, emailType, actorId: user.id });
    setBusy(null);
    if (!res.ok) {
      // The reason is already in the console with the server's own words; this is
      // the short version for the person standing at the screen.
      const why = {
        no_session: isAr ? 'انتهت جلستك — أعد تسجيل الدخول' : 'Your session expired — sign in again',
        network: isAr ? 'تعذّر الوصول للخادم' : 'Could not reach the server',
      }[res.error] || String(res.error || '');
      flash(`${isAr ? 'لم تُرسل: ' : 'Not sent: '}${why}`);
      return;
    }
    flash(isAr ? 'أُرسلت الرسالة' : 'Email sent');
    onSent?.();
  };

  const runWhatsApp = async () => {
    setBusy('whatsapp');
    const res = await sendOutreach({ channel: 'whatsapp', row, message: waMessage, actorId: user.id });
    setBusy(null);
    if (!res.ok) {
      flash(isAr ? 'لا يوجد رقم واتساب لهذا المورّد' : 'No WhatsApp number on file');
      return;
    }
    window.open(res.href, '_blank', 'noopener,noreferrer');
    onSent?.();
  };

  return (
    <div className="ac-card">
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <p className="ac-card-title">{isAr ? 'مراسلة المورّد' : 'Contact the supplier'}</p>
        {last && (
          <p className="acv-lastreach">
            {isAr ? 'آخر تواصل ' : 'Last contacted '}{fmtWhen(last.created_at, isAr)}
            {last.action === 'supplier_outreach_whatsapp_opened'
              ? (isAr ? ' · واتساب' : ' · WhatsApp')
              : (isAr ? ' · بريد' : ' · Email')}
          </p>
        )}
      </div>

      {/* WhatsApp — works today, and is the channel this audience actually reads */}
      <div className="acv-reach-row">
        <div style={{ minWidth: 0, flex: 1 }}>
          <p className="acv-reach-label">{isAr ? 'واتساب' : 'WhatsApp'}</p>
          <p className="acv-reach-sub" dir="ltr" style={{ textAlign: isAr ? 'right' : 'left' }}>
            {waNumber || (isAr ? 'لا يوجد رقم' : 'no number on file')}
          </p>
        </div>
        <button type="button" className="ac-btn ac-btn-sm" onClick={() => setPreview((v) => !v)}>
          {preview ? (isAr ? 'إخفاء النص' : 'Hide text') : (isAr ? 'عرض النص' : 'Show text')}
        </button>
        <button type="button" className="ac-btn ac-btn-sm ac-btn-primary" disabled={!waNumber || busy === 'whatsapp'}
                onClick={runWhatsApp}>
          {busy === 'whatsapp' ? '…' : (isAr ? 'فتح المحادثة ↗' : 'Open chat ↗')}
        </button>
      </div>
      {preview && <pre className="acv-reach-preview">{waMessage}</pre>}
      <p className="acv-reach-note">
        {isAr
          ? 'يفتح واتسابك بالنص جاهزًا — الإرسال منك. الإرسال الآلي يحتاج قالبًا معتمدًا من Meta.'
          : 'This opens your WhatsApp with the text ready — you press send. Automated sending needs a Meta-approved template.'}
      </p>

      {/* Email — the engine already existed; this is the button it never had */}
      <div className="acv-reach-row" style={{ marginTop: 14 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <p className="acv-reach-label">{isAr ? 'بريد' : 'Email'}</p>
          <p className="acv-reach-sub" dir="ltr" style={{ textAlign: isAr ? 'right' : 'left' }}>
            {row.email || (isAr ? 'لا يوجد بريد' : 'no email on file')}
          </p>
        </div>
        <select className="ac-input acv-reach-select" value={emailType} onChange={(e) => setEmailType(e.target.value)}>
          {OUTREACH_EMAIL_TYPES.map((t) => (
            <option key={t.key} value={t.key}>
              {(isAr ? t.ar : t.en)}{t.key === suggested ? (isAr ? ' — مقترح' : ' — suggested') : ''}
            </option>
          ))}
        </select>
        <button type="button" className="ac-btn ac-btn-sm ac-btn-primary" disabled={!row.email || busy === 'email'}
                onClick={runEmail}>
          {busy === 'email' ? '…' : (isAr ? 'إرسال' : 'Send')}
        </button>
      </div>
    </div>
  );
}
